/**
 * A ranked selector path for anchoring ONE element across renders
 * (Issue #13575, epic #13573).
 *
 * A feedback capture points at an element; the pointer is stored and has to
 * find the same element again later, after React has re-rendered and possibly
 * re-mounted the tree. `outerHTML` cannot do that, and neither can a CSS class
 * path: this app's classes are Tailwind utilities (`flex items-center`), which
 * name a hundred elements and mean nothing, and its component library writes
 * INTERACTION state into `data-state` / `data-orientation` / `data-side`, which
 * change under the user's hands. So this module never emits a class, and never
 * emits a denylisted state attribute.
 *
 * THE RUNG ORDER — first rung that resolves to exactly one element wins:
 *
 *   1. `id`      — a stable id, i.e. one `looksGenerated()` does not reject.
 *   2. `testid`  — `data-testid` / `data-cy`, the app's own test hooks.
 *   3. `attribute` — an authored attribute: `data-surface`, `name`,
 *      `aria-label`, `alt`, then any other `data-*` that is neither
 *      denylisted nor carrying a bare number (which is a position, not a
 *      name).
 *   4. `text`    — a leaf's normalised text, <= 60 chars and free of digits
 *      (a count or a date is text that changes without the element changing).
 *   5. `structural` — a `tag:nth-of-type(n)` chain, flagged `fragile: true`,
 *      anchored on the nearest ancestor with a rung 1-3 path and rooted at
 *      `<html>` when there is none, or `UNRESOLVABLE_PATH` when even that
 *      chain does not resolve back to the element.
 *
 * Every rung is VERIFIED before it is returned — RUNG 5 INCLUDED: the
 * candidate selector is run against the element's own document and accepted
 * only when it matches exactly one element and that element is the one asked
 * about. That is what makes the fall-through cases correct rather than
 * hopeful — a duplicated `data-testid`, an id the host's selector engine
 * cannot parse, or two leaves with the same text all fail the check and drop
 * to the next rung instead of emitting a path that resolves to the wrong node.
 *
 * Rung 5 has nothing below it to fall to, so when its chain does not verify it
 * emits `UNRESOLVABLE_PATH` instead. That case is reachable: an element that
 * is detached, inside a shadow root, or owned by another document has a chain
 * rooted at something other than `documentElement`, which makes it RELATIVE —
 * and a relative chain handed to `document.querySelector` binds to the first
 * thing that happens to match. Returning a path that resolves to an unrelated
 * live element is strictly worse than returning one that resolves to null,
 * because null is an answer the caller already has to handle.
 *
 * WHAT A PATH CAPTURES, for whoever persists one. Rung 3 copies an attribute
 * value verbatim into `path` and `label`, and this app writes
 * `data-school-name`, `data-conversation-id`, `data-review-period-id` and
 * friends; rung 4 embeds a leaf's text, and `label` embeds up to
 * `MAX_TEXT_LENGTH` characters of any element's text. So a stored
 * `SelectorPath` can contain page content — including 1:1 and review copy —
 * and inherits whatever org scoping the table it lands in has. Treat it as
 * user data, not as an opaque handle.
 *
 * NOTHING CONSUMES THIS YET, and no gate watches it: the unit suite runs under
 * happy-dom, whose selector parser is more permissive than a browser's, and
 * `scripts/gates/golden-path-e2e` cannot cover a module with no rendered path.
 * The first consumer should bring a browser-level check with it.
 *
 * DEPENDENCY-FREE ON PURPOSE. No React, no app modules, no framework: the only
 * import is the sibling constants file, which is data. The module reads the
 * DOM through standard APIs and nothing else, so its source can later be
 * serialised (`Function.prototype.toString()`) and injected into a page that
 * does not run this bundle — the Lightspeed discipline this is ported from.
 * `CSS.escape` is deliberately NOT used for the same reason: it is absent in
 * jsdom and in older embedded webviews, and a fallback that disagreed with it
 * would make the emitted path a function of the host. The escape below is
 * self-contained and identical everywhere.
 */

import {
  AUTHORED_ATTRIBUTES,
  DENYLISTED_ATTRIBUTES,
  DENYLISTED_ATTRIBUTE_PREFIXES,
  LABEL_SEPARATOR,
  MAX_ATTRIBUTE_VALUE_LENGTH,
  MAX_TEXT_LENGTH,
  TEST_HOOK_ATTRIBUTES,
  UNRESOLVABLE_PATH,
} from './selector-path.constants'

/** Which rung produced a path — the caller's cue for how much to trust it. */
export type SelectorPathKind =
  | 'id'
  | 'testid'
  | 'attribute'
  | 'text'
  | 'structural'

export interface SelectorPath {
  /** The path itself. Feed it back to `resolveSelectorPath`. */
  path: string
  /** The rung that produced it. */
  kind: SelectorPathKind
  /** True when the path depends on position, so a re-order breaks it. */
  fragile: boolean
  /** A human label for UI: an authored value, or `tag · text`. */
  label: string
}

/**
 * The marker for the one non-CSS path form. CSS has no text selector, so the
 * text rung emits `li::text("Save changes")` — a real type selector, then this
 * suffix carrying a JSON-quoted string. JSON because it round-trips every
 * character (quotes, backslashes, newlines) through `JSON.parse` with no
 * hand-written unescaping.
 */
const TEXT_MARKER = '::text('

/** Collapses every whitespace run to one space and trims. */
function normaliseText(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

/**
 * Whether an id or attribute value was minted by a framework rather than
 * written by a person.
 *
 * A generated value is stable within one mount and different after the next
 * one, which is the worst possible anchor: it verifies perfectly at capture
 * time and resolves to nothing afterwards. The shapes, in order:
 * React 18 / Radix `useId` (`:r3:`, `radix-:r1:`), React 19 `useId`
 * (`_R_1H2_`), its pre-release guillemet form (`«r0»`), a uuid, and a
 * hash-like hex run of 8 or more characters.
 *
 * TWO RULES KEEP IT OFF HUMAN COPY, which matters because `isUsableValue`
 * runs it over every attribute value and `AUTHORED_ATTRIBUTES` includes
 * `aria-label`, `alt` and `name`:
 *
 *  - A value containing whitespace is prose, never a generated identifier.
 *    Without this, `aria-label="Meeting 10:30:00"` reads as a `useId` token
 *    (`:30:`) and an icon-only button loses a perfectly stable anchor to the
 *    fragile rung, silently — the caller sees `fragile: true` and cannot tell
 *    it was a false positive.
 *  - A hex run must contain BOTH a digit and an `a`-`f` letter. A date is
 *    eight hex characters (`20240115`) and a hash essentially never has no
 *    letters in it, so requiring the mix keeps `report-20240115` usable while
 *    still rejecting `a1b2c3d4`. The letter half is what English words that
 *    happen to be hexadecimal (`deadbeef`, `facade`) fail on, which is the
 *    same trade in the other direction.
 *
 * @example
 * looksGenerated(':r3:')                    // true  — React useId
 * looksGenerated('radix-:r1:')              // true  — Radix useId
 * looksGenerated('a1b2c3d4')                // true  — hash-like
 * looksGenerated('Delete check-in 20240115') // false — prose with a date
 * looksGenerated('submit-button')           // false — authored
 */
export function looksGenerated(value: string): boolean {
  // Prose, not an identifier: no generated id has a space in it.
  if (/\s/.test(value)) return false
  if (/:[a-z0-9]+:/i.test(value)) return true
  if (/«[^»]*»/.test(value)) return true
  if (/_r_[a-z0-9]*_/i.test(value)) return true
  if (
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(value)
  ) {
    return true
  }
  const hexRuns = value.match(/[0-9a-f]{8,}/gi) ?? []
  return hexRuns.some(run => /\d/.test(run) && /[a-f]/i.test(run))
}

/**
 * Escapes a CSS identifier without `CSS.escape` (see the header).
 *
 * Anything outside `[A-Za-z0-9_-]` and the non-ASCII range is backslash
 * escaped, and a digit that would start the identifier becomes a hex escape,
 * which is the one case a backslash cannot express. Selector engines vary in
 * how much of this they honour — the uniqueness check is what covers the
 * difference, since a path the host cannot parse simply fails verification
 * and the next rung runs.
 *
 * "Would start the identifier" is TWO positions, not one. CSS lets an ident
 * begin with `-`, but `-` followed by a digit is not a valid start, so both
 * `9foo` and `-9foo` need the escape — and only the first was handled until
 * happy-dom's permissive parser stopped hiding it. The hex form works out
 * because the ASCII codes of `0`-`9` are `0x30`-`0x39`, so `\3` plus the
 * digit character IS its own hex escape; the trailing space terminates it.
 *
 * EXPORTED because its output is the one part of this module no round-trip
 * test can observe. happy-dom, which the unit suite runs under, does not
 * honour CSS escapes at all — `#\31 abc` matches nothing there, silently,
 * while `#-1abc` matches happily even though a browser rejects it. So under
 * happy-dom a correctly escaped id loses the `id` rung and falls through,
 * and an incorrectly escaped one wins it. Asserting the emitted string is the
 * only way to tell those apart, and the fall-through is the safe half of the
 * trade: the browser this actually runs in parses the escape and anchors on
 * the id.
 *
 * @example
 * escapeIdentifier('save-button')  // 'save-button'
 * escapeIdentifier('1abc')         // '\\31 abc'
 * escapeIdentifier('-1abc')        // '-\\31 abc'
 * escapeIdentifier('a.b')          // 'a\\.b'
 */
export function escapeIdentifier(value: string): string {
  let out = ''
  for (let i = 0; i < value.length; i++) {
    const char = value[i]
    const isSafe = /[a-zA-Z0-9_-]/.test(char) || char.charCodeAt(0) > 0x7f
    if (!isSafe) {
      out += `\\${char}`
      continue
    }
    const startsIdentifier = i === 0 || (i === 1 && value[0] === '-')
    if (startsIdentifier && /[0-9]/.test(char)) {
      out += `\\3${char} `
      continue
    }
    out += char
  }
  return out
}

/**
 * Escapes a value for a quoted CSS string.
 *
 * `"` and `\` can break out of the quotes. The control characters cannot, but
 * a CSS string may not contain a raw newline at all — a browser treats one as
 * a parse error while happy-dom accepts it, which is exactly the host-dependent
 * behaviour this module's escaping exists to avoid. They are emitted in CSS's
 * own `\<hex> ` form; the trailing space terminates the escape and is consumed
 * by the parser, so a literal space that follows survives as a second one.
 *
 * Exported for the same reason as `escapeIdentifier` — see its note on why
 * happy-dom cannot observe this property through a round trip.
 *
 * The control characters are matched by CODE POINT rather than by a character
 * class. `noControlCharactersInRegex` is an error in this repo and there is no
 * literal spelling of that class it accepts — a `biome-ignore` would only move
 * the problem, and a loop says what it means anyway.
 *
 * @example
 * escapeAttributeValue('Save "now"')   // 'Save \\"now\\"'
 * escapeAttributeValue('line1\nline2') // 'line1\\a line2'
 */
export function escapeAttributeValue(value: string): string {
  let out = ''
  for (const char of value) {
    if (char === '"' || char === '\\') {
      out += `\\${char}`
      continue
    }
    const code = char.charCodeAt(0)
    if (code < 0x20 || code === 0x7f) {
      out += `\\${code.toString(16)} `
      continue
    }
    out += char
  }
  return out
}

/**
 * An element's type selector.
 *
 * `localName`, not `tagName`: for an HTML element the two differ only in case
 * and `localName` is already the lowercase form a selector wants, while for a
 * foreign (SVG / MathML) element `localName` preserves the camelCase that
 * `linearGradient` needs and `tagName.toLowerCase()` would destroy.
 */
function typeToken(el: Element): string {
  return escapeIdentifier(el.localName)
}

/**
 * The document an element belongs to — the one a path for it must be verified
 * against, and the one `resolveSelectorPath` has to be pointed at to read it
 * back. `Element.ownerDocument` is non-nullable in `lib.dom`, so there is no
 * fallback to the ambient `document` here: an element created by another
 * document's `createElement` belongs to that document, and quietly verifying
 * against a different tree is the bug this indirection exists to prevent.
 */
function documentOf(el: Element): Document {
  return el.ownerDocument
}

/** Whether `selector` matches `el` and nothing else. */
function matchesOnly(el: Element, selector: string): boolean {
  try {
    const found = documentOf(el).querySelectorAll(selector)
    return found.length === 1 && found[0] === el
  } catch {
    // An identifier the host's selector engine will not parse. Not usable,
    // not fatal: the caller falls to the next rung.
    return false
  }
}

/** Whether an attribute value can carry a path at all. */
function isUsableValue(value: string | null): value is string {
  if (value === null) return false
  const trimmed = value.trim()
  if (trimmed.length === 0) return false
  if (trimmed.length > MAX_ATTRIBUTE_VALUE_LENGTH) return false
  return !looksGenerated(trimmed)
}

function isDenylisted(name: string): boolean {
  if (DENYLISTED_ATTRIBUTES.includes(name)) return true
  return DENYLISTED_ATTRIBUTE_PREFIXES.some(prefix => name.startsWith(prefix))
}

/**
 * Rung 3's candidates in preference order: the named allowlist first, then
 * any other `data-*` attribute that is neither a test hook (rung 2 already
 * had its turn) nor denylisted.
 *
 * Accepting unknown `data-*` attributes is what makes the denylist
 * load-bearing rather than decorative: `data-section="header"` is a good
 * anchor nobody could have enumerated in advance, and `data-state="open"` is
 * a trap that has to be named to be avoided.
 */
function authoredAttributes(el: Element): [string, string][] {
  const out: [string, string][] = []
  const seen = new Set<string>()
  for (const name of AUTHORED_ATTRIBUTES) {
    const value = el.getAttribute(name)
    if (isUsableValue(value)) {
      out.push([name, value.trim()])
      seen.add(name)
    }
  }
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name
    if (seen.has(name)) continue
    if (!name.startsWith('data-')) continue
    if (TEST_HOOK_ATTRIBUTES.includes(name)) continue
    if (isDenylisted(name)) continue
    if (!isUsableValue(attr.value)) continue
    // A bare number is a position or a count, not a name. The denylist names
    // the two attributes this app actually uses for it, but the shape is what
    // makes the rule general: anything whose value is only digits is as
    // reorder-fragile as the `:nth-of-type` chain rung 5 flags `fragile:
    // true`, and rung 3 reports `fragile: false`.
    if (/^-?\d+$/.test(attr.value.trim())) continue
    out.push([name, attr.value.trim()])
  }
  return out
}

function attributeSelector(name: string, value: string): string {
  return `[${name}="${escapeAttributeValue(value)}"]`
}

/**
 * Rungs 1-3 for one element, or null. Used both for the element being
 * anchored and — in the structural fallback — for its ancestors.
 */
function cssRung(
  el: Element,
): { path: string; kind: SelectorPathKind; label: string } | null {
  const id = el.getAttribute('id')
  if (isUsableValue(id)) {
    const path = `#${escapeIdentifier(id.trim())}`
    if (matchesOnly(el, path)) {
      return { path, kind: 'id', label: id.trim() }
    }
  }
  for (const name of TEST_HOOK_ATTRIBUTES) {
    const value = el.getAttribute(name)
    if (!isUsableValue(value)) continue
    const path = attributeSelector(name, value.trim())
    if (matchesOnly(el, path)) {
      return { path, kind: 'testid', label: value.trim() }
    }
  }
  for (const [name, value] of authoredAttributes(el)) {
    const path = attributeSelector(name, value)
    if (matchesOnly(el, path)) {
      return { path, kind: 'attribute', label: value }
    }
  }
  return null
}

/**
 * Rung 4's text, or null.
 *
 * Leaf-only, because a container's `textContent` is its children's and would
 * anchor the wrong node. Digit-free, because a count, a date or a currency is
 * text that changes while the element does not — those elements take the
 * structural rung, which the header's edge case 2 asks to anchor on the
 * nearest authored ancestor instead.
 */
function leafText(el: Element): string | null {
  if (el.children.length > 0) return null
  const text = normaliseText(el.textContent ?? '')
  if (text.length === 0 || text.length > MAX_TEXT_LENGTH) return null
  if (/\d/.test(text)) return null
  return text
}

/** Every leaf under `prefix` whose normalised text equals `text`. */
function textMatches(doc: Document, prefix: string, text: string): Element[] {
  let candidates: Element[]
  try {
    candidates = Array.from(doc.querySelectorAll(prefix))
  } catch {
    return []
  }
  return candidates.filter(
    candidate =>
      candidate.children.length === 0 &&
      normaliseText(candidate.textContent ?? '') === text,
  )
}

/** `tag` alone, or `tag · text` truncated to the text ceiling. */
function labelFor(el: Element): string {
  const tag = el.localName
  const text = normaliseText(el.textContent ?? '')
  if (text.length === 0) return tag
  const shown =
    text.length > MAX_TEXT_LENGTH
      ? `${text.slice(0, MAX_TEXT_LENGTH - 1)}…`
      : text
  return `${tag}${LABEL_SEPARATOR}${shown}`
}

/**
 * This element's 1-based position among same-type siblings.
 *
 * Counted up to `el` and returned once, rather than returned from inside the
 * loop with a fallback after it: the fallback would be unreachable for any
 * element that has a parent, and unreachable code in the one function whose
 * off-by-one breaks every structural path is worse than no code.
 */
function indexOfType(el: Element): number {
  const parent = el.parentElement
  if (!parent) return 1
  let index = 1
  for (const sibling of Array.from(parent.children)) {
    if (sibling === el) break
    if (sibling.localName === el.localName) index++
  }
  return index
}

/**
 * Rung 5. A child-combinator chain, shortened by anchoring on the nearest
 * ancestor that has a rung 1-3 path of its own.
 *
 * Anchoring is what keeps a portal usable (edge case 1): a modal rendered at
 * the end of `<body>` has no meaningful ancestry, so without an anchor the
 * chain runs all the way to `<html>` — correct, still resolvable while the
 * portal is mounted, and about as fragile as it sounds. Hence
 * `fragile: true` on every path this produces.
 *
 * VERIFIED like every rung above it, and this is the rung where it matters
 * most, because there is nothing below to fall to. The walk stops at whatever
 * the topmost ancestor turns out to be, which is `documentElement` for an
 * element in the page and something else entirely for one that is detached,
 * inside a shadow root, or owned by another document. In those cases the
 * chain is relative: `div > span:nth-of-type(1)` handed to
 * `document.querySelector` binds to the first matching element in the live
 * page, which is how an unverified rung 5 returned a pointer to an unrelated
 * node rather than the honest null the API already supports. When the chain
 * does not verify, `UNRESOLVABLE_PATH` goes out instead.
 */
function structuralPath(el: Element): SelectorPath {
  const steps: string[] = []
  let anchor: string | null = null
  let current: Element = el
  for (;;) {
    const parent = current.parentElement
    if (!parent) {
      // The root element: nothing to be the nth of.
      steps.unshift(typeToken(current))
      break
    }
    steps.unshift(`${typeToken(current)}:nth-of-type(${indexOfType(current)})`)
    const parentRung = cssRung(parent)
    if (parentRung) {
      anchor = parentRung.path
      break
    }
    current = parent
  }
  const chain = steps.join(' > ')
  const path = anchor === null ? chain : `${anchor} > ${chain}`
  return {
    path: matchesOnly(el, path) ? path : UNRESOLVABLE_PATH,
    kind: 'structural',
    fragile: true,
    label: labelFor(el),
  }
}

/**
 * The best available path to `el`, with the rung that produced it.
 *
 * Total: this never throws and never returns null. It can return
 * `UNRESOLVABLE_PATH` — see `structuralPath` — for an element that is not
 * addressable from its own document, which is a real answer rather than a
 * failure: the caller finds out at capture time that the anchor is no good,
 * instead of finding out later by resolving to the wrong element.
 *
 * `fragile` is true only on the last rung.
 *
 * @example
 * computeSelectorPath(button)
 * // { path: '[data-testid="save"]', kind: 'testid',
 * //   fragile: false, label: 'save' }
 *
 * @example
 * // No id, no test hook, no authored attribute, digit-free leaf text:
 * computeSelectorPath(cell)
 * // { path: 'li::text("Save changes")', kind: 'text',
 * //   fragile: false, label: 'li · Save changes' }
 */
export function computeSelectorPath(el: Element): SelectorPath {
  const rung = cssRung(el)
  if (rung) {
    return { ...rung, fragile: false }
  }
  const text = leafText(el)
  if (text !== null) {
    const prefix = typeToken(el)
    const matches = textMatches(documentOf(el), prefix, text)
    if (matches.length === 1 && matches[0] === el) {
      return {
        path: `${prefix}${TEXT_MARKER}${JSON.stringify(text)})`,
        kind: 'text',
        fragile: false,
        label: labelFor(el),
      }
    }
  }
  return structuralPath(el)
}

/** Splits a `tag::text("…")` path, or null when it is not one. */
function parseTextPath(path: string): { prefix: string; text: string } | null {
  const at = path.indexOf(TEXT_MARKER)
  if (at === -1 || !path.endsWith(')')) return null
  const prefix = path.slice(0, at)
  const quoted = path.slice(at + TEXT_MARKER.length, -1)
  try {
    const text: unknown = JSON.parse(quoted)
    if (typeof text !== 'string') return null
    return { prefix, text }
  } catch {
    return null
  }
}

/**
 * The element a path points at now, or null.
 *
 * Null is a real answer, not an error: the element a stored path names may
 * legitimately be gone.
 *
 * AMBIGUITY IS ALSO NULL, and it is not enough that the compute side checked.
 * `computeSelectorPath` refuses to emit a path matching more than one element
 * *at capture time*, but a stored path is read back against a page that has
 * changed since — that is the whole point of storing one. A second row can
 * acquire the same `data-testid`, or the copy a text path names can appear
 * twice. Returning the first of several matches there is a coin flip dressed
 * as an answer, and picking the wrong one is the failure the rung order
 * exists to prevent. So both forms require exactly one match.
 *
 * `UNRESOLVABLE_PATH` short-circuits to null before any lookup. It is a real
 * attribute selector, so a page that happened to carry that attribute would
 * otherwise resolve a deliberately-unresolvable capture to an unrelated
 * element — the sentinel re-creating, one step later, the bug it was added to
 * fix.
 *
 * `doc` defaults to the ambient document, which is what a page-level capture
 * wants. It is a parameter because the compute side verifies against
 * `el.ownerDocument`: for an element in an `<iframe>` the two halves are
 * otherwise reading different trees, and a path "verified" in one document
 * would be applied to another.
 *
 * @example
 * resolveSelectorPath('[data-testid="save"]')     // the save button, or null
 * resolveSelectorPath('li::text("Save changes")') // the matching leaf
 * resolveSelectorPath('[data-testid="row"]')      // null if two rows match
 * resolveSelectorPath(UNRESOLVABLE_PATH)          // always null, by design
 */
export function resolveSelectorPath(
  path: string,
  doc: Document = document,
): Element | null {
  if (path === UNRESOLVABLE_PATH) return null
  const textPath = parseTextPath(path)
  if (textPath) {
    const matches = textMatches(doc, textPath.prefix, textPath.text)
    return matches.length === 1 ? matches[0] : null
  }
  try {
    const found = doc.querySelectorAll(path)
    return found.length === 1 ? found[0] : null
  } catch {
    return null
  }
}
